import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useId } from "react";
import Accordion from "@/components/campaignBuilder/Accordion";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { ConditionalPanel, RadioOption, StyledInput, StyledSelect } from "./shared";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
}

// Activation can't sit more than 6 months out: activation starts the recipient's clock, and pushing
// it past the halfway mark of the credit's 12-month life would leave under 6 months to redeem.
const ACTIVATION_MAX_MONTHS = 6;

/** A Date → local `YYYY-MM-DD` (never toISOString, which shifts to UTC and can land a day off). */
function toLocalIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function todayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toLocalIso(d);
}

function maxActivationDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + ACTIVATION_MAX_MONTHS);
  return d;
}

/**
 * Link Activation — manual-send only, taking the Schedule accordion's slot. The sender downloads the
 * links at launch, but they stay dormant until this moment: immediate (live at launch) or a picked
 * date/time. Reuses the scheduled_* columns server-side (see the plan doc).
 */
export default function LinkActivation({ isOpen, onToggle }: Props) {
  const {
    activationMode,
    setActivationMode,
    activationDate,
    setActivationDate,
    activationTime,
    setActivationTime,
    activationTz,
    setActivationTz,
  } = useCampaignBuilder();

  const errorId = useId();
  const isScheduled = activationMode === "scheduled";
  const minIso = todayIso();
  const maxDate = maxActivationDate();
  const maxIso = toLocalIso(maxDate);
  const maxPretty = maxDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  // Past dates are blocked with an inline error rather than silently clamped (prototype parity).
  const datePast = isScheduled && !!activationDate && activationDate < minIso;

  const description = isScheduled
    ? activationDate
      ? `Activates ${activationDate}`
      : "Activate on a certain date"
    : "Activate links immediately";

  return (
    <Accordion
      title="Link Activation"
      description={description}
      icon={<LinkIcon sx={{ fontSize: 24 }} />}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-2 mt-4">
        <RadioOption
          selected={!isScheduled}
          onSelect={() => setActivationMode("immediate")}
          label="Activate links immediately"
          hint="Gift links work as soon as the campaign launches. Download them and distribute whenever you're ready."
        />
        <RadioOption
          selected={isScheduled}
          onSelect={() => setActivationMode("scheduled")}
          label="Activate on a certain date"
          hint="Download your links now and share them ahead of time — they stay dormant until the date you pick. (Perfect for when you want to distribute links printed as QR codes on promotional materials or event swag.)"
          hasVisiblePanel={isScheduled}
        />
        <ConditionalPanel visible={isScheduled} merged>
          <p className="font-lato text-[12px] font-bold uppercase tracking-[1px] text-graphite mb-2">
            Links go live on:
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <StyledInput
              type="date"
              value={activationDate}
              min={minIso}
              max={maxIso}
              onChange={(e) => setActivationDate(e.target.value)}
              aria-invalid={datePast}
              aria-describedby={datePast ? errorId : undefined}
              style={datePast ? { border: "1px solid var(--color-good-wine)" } : undefined}
            />
            <StyledInput
              type="time"
              value={activationTime}
              onChange={(e) => setActivationTime(e.target.value)}
            />
            <StyledSelect value={activationTz} onChange={(e) => setActivationTz(e.target.value)}>
              <option value="PT">Pacific (PT)</option>
              <option value="MT">Mountain (MT)</option>
              <option value="CT">Central (CT)</option>
              <option value="ET">Eastern (ET)</option>
            </StyledSelect>
          </div>
          {datePast ? (
            <p
              id={errorId}
              role="alert"
              className="mt-2 mb-0 font-lato text-[12.5px] font-semibold text-good-wine"
            >
              The activation date can't be in the past.
            </p>
          ) : (
            <p className="mt-2 mb-0 font-lato text-[12px] text-graphite">
              Can't be more than {ACTIVATION_MAX_MONTHS} months from today, so the campaign still
              finishes within a year of setup (latest: {maxPretty}).
            </p>
          )}
          <div className="mt-3 flex items-start gap-2">
            <InfoOutlinedIcon
              sx={{ fontSize: 16, color: "var(--color-graphite)", marginTop: "1px" }}
            />
            <p className="font-lato text-[12px] text-graphite leading-relaxed">
              Anyone who opens a link before this date sees a "not yet active" message with the
              activation date.
            </p>
          </div>
        </ConditionalPanel>
      </div>
    </Accordion>
  );
}
